import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../../../../../components/button/button";
import RadioGroup from "../../../../../../components/radio-group/radio-group";
import Select from "../../../../../../components/select/select";
import { useLineups } from "../../../../../../hooks/use-lineups";
import { useSubstitutions } from "../../../../../../hooks/use-substitutions";
import { openXShareIntent } from "../../../../../../lib/x-share";
import { cardRequest } from "../../../../../../redux/broadcast/broadcast.actions";
import {
	selectAwayTeam,
	selectBroadcastId,
	selectFixtureId,
	selectHomeTeam,
} from "../../../../../../redux/broadcast/broadcast.selectors";
import UpdatesSocial from "../../components/social/updates-social";
import UpdatesTextArea from "../../components/updates-textarea/updates-textarea";
import useSocialShare from "../../hooks/use-social-share";
import useUpdatesText from "../../hooks/use-updates-text";

type CardTypes = "yellow" | "2Yellow" | "red";

const CARD_LABELS: Record<CardTypes, { emoji: string; label: string }> = {
	yellow: { emoji: "🟨", label: "Yellow card" },
	"2Yellow": { emoji: "🟨🟥", label: "Second yellow card" },
	red: { emoji: "🟥", label: "Red card" },
};

const CardsUpdatesControls = () => {
	const dispatch = useDispatch();
	const { setUpdateText, updateText } = useUpdatesText();
	const homeTeam = useSelector(selectHomeTeam);
	const awayTeam = useSelector(selectAwayTeam);
	const fixtureId = useSelector(selectFixtureId);
	const broadcastId = useSelector(selectBroadcastId);

	const lineups = useLineups(fixtureId);
	const substitutions = useSubstitutions(broadcastId);

	const [activeTab, setActiveTab] = useState<CardTypes>("yellow");
	const [playerId, setPlayerId] = useState("");
	const social = useSocialShare();

	type SelectedPlayer = {
		name: string;
		team: "home" | "away";
		teamId?: number;
		playerId?: number;
	};

	const { groups: playerGroups, lookup: playerLookup } = useMemo(() => {
		const lookup = new Map<string, SelectedPlayer>();

		if (!lineups) return { groups: [], lookup };

		const toOptions = (
			players: typeof lineups.home.startXI,
			team: "home" | "away",
			teamId: number,
		) =>
			players.map((player) => {
				const value = String(player.id);
				lookup.set(value, { name: player.name, team, teamId, playerId: player.id });
				return { value, label: `${player.name} - ${player.number}` };
			});

		const buildGroups = (team: "home" | "away", teamName: string) => {
			const teamLineup = lineups[team];
			const teamSubs = substitutions.filter((s) => s.team === team);
			const subbedOff = new Set(
				teamSubs.flatMap((s) => s.subs.map((p) => p.playerOut)),
			);
			const subbedOn = new Set(
				teamSubs.flatMap((s) => s.subs.map((p) => p.playerIn)),
			);

			const onPitch = teamLineup.startXI
				.filter((p) => !subbedOff.has(p.name))
				.concat(teamLineup.substitutes.filter((p) => subbedOn.has(p.name)))
				.sort((a, b) => a.number - b.number);

			const onBench = teamLineup.substitutes
				.filter((p) => !subbedOn.has(p.name))
				.sort((a, b) => a.number - b.number);

			const groups = [
				{ label: teamName, options: toOptions(onPitch, team, teamLineup.teamId) },
			];
			if (onBench.length > 0) {
				groups.push({
					label: `${teamName} substitutes`,
					options: toOptions(onBench, team, teamLineup.teamId),
				});
			}
			if (teamLineup.coach) {
				const value = `${team}-coach`;
				lookup.set(value, { name: teamLineup.coach, team, teamId: teamLineup.teamId });
				groups.push({
					label: `${teamName} management`,
					options: [{ value, label: teamLineup.coach }],
				});
			}
			return groups;
		};

		return {
			groups: [...buildGroups("home", homeTeam), ...buildGroups("away", awayTeam)],
			lookup,
		};
	}, [lineups, substitutions, homeTeam, awayTeam]);

	const handlePostUpdate = () => {
		if (!broadcastId) return;

		const selected = playerLookup.get(playerId);
		if (!selected) return;

		const comment = updateText?.trim();
		const teamName = selected.team === "home" ? homeTeam : awayTeam;

		dispatch(
			cardRequest({
				broadcastId,
				team: selected.team,
				teamId: selected.teamId,
				player: selected.name,
				playerId: selected.playerId,
				type: activeTab,
				comment: comment || undefined,
				share: social.blueSky,
				hashtags: social.parsedHashtags,
			}),
		);

		if (social.postToX) {
			const { emoji, label } = CARD_LABELS[activeTab];
			openXShareIntent(
				[`${emoji} ${label} for ${selected.name} (${teamName})`, comment]
					.filter(Boolean)
					.join("\n"),
				broadcastId,
				social.parsedHashtags,
			);
		}

		setUpdateText("");
		setPlayerId("");
		social.reset();
	};

	return (
		<>
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					marginBottom: "1.2rem",
				}}
			>
				<div
					style={{
						width: "60%",
						display: "flex",
						alignItems: "center",
					}}
				>
					<RadioGroup
						items={[
							{
								id: "yellow",
								disabled: false,
								label: "Yellow card",
							},
							{
								id: "2Yellow",
								disabled: false,
								label: "2nd yellow card",
							},
							{
								id: "red",
								disabled: false,
								label: "Red card",
							},
						]}
						activeId={activeTab}
						onChange={(tab) => {
							setActiveTab(tab as CardTypes);
							setPlayerId("");
						}}
					/>
				</div>
				<div style={{ width: "40%" }}>
					<Select
						value={playerId}
						onChange={setPlayerId}
						placeholder="Select player"
						groups={playerGroups}
					/>
				</div>
			</div>
			<UpdatesTextArea
				value={updateText}
				onChange={(value) => setUpdateText(value)}
				onSubmit={handlePostUpdate}
				rows={4}
			/>
			<div
				style={{
					display: "flex",
					alignItems: "flex-end",
				}}
			>
				<UpdatesSocial
					blueSky={social.blueSky}
					onBlueSkyChange={social.setBlueSky}
					postToX={social.postToX}
					onPostToXChange={social.setPostToX}
					hashtags={social.hashtags}
					onHashtagsChange={social.setHashtags}
				/>
				<Button onClick={handlePostUpdate}>Post update</Button>
			</div>
		</>
	);
};

export default CardsUpdatesControls;
